import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { format, parseISO } from 'date-fns';
import { Box, Button, Divider, Typography } from '@mui/material';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';

const OrderSummary = ({
  createPtoData,
  chainContactIds,
  currentAffiliate,
  currentLocation,
  currentProduct,
  detailsExpanded,
  headerButton,
  headerTitle,
  onHeaderClick,
  originatorLocationProductOrigins,
  userAffiliates,
}) => {
  const {
    chained,
    inventorySelection,
    originIds,
    originatorIdName,
    reconsignmentControl,
    referenceId,
    rvp,
    transferDate,
    volume,
  } = createPtoData;
  const { customerNameShort: originator } = userAffiliates.find(
    affiliate => affiliate.customerIdName === originatorIdName
  );

  const { customerNameLong: recipientIdNameLong = '' } = currentAffiliate;
  const { locationNameLong = '' } = currentLocation;
  const { productNameLong = '' } = currentProduct;

  const originsToDisplay = originatorLocationProductOrigins.filter(({ originId }) =>
    originIds.includes(originId)
  );

  const truncate = (string, lengthLimit) => {
    if (string.length > lengthLimit) {
      return `${string.substring(0, lengthLimit - 3)}...`;
    }
    return string;
  };

  const LENGTH_LIMIT = 23;

  const summaryFieldsMap = {
    Details: [
      {
        classNames: 'css-heap-redact',
        id: 1,
        label: 'Originator',
        value: originator,
      },
      {
        classNames: 'css-heap-redact',
        id: 2,
        label: 'Recipient',
        value: recipientIdNameLong,
      },
      {
        id: 3,
        label: 'Location',
        value: locationNameLong,
      },
      {
        classNames: 'css-heap-redact',
        id: 4,
        label: 'Product',
        value: `${productNameLong} ${rvp || ''}`,
      },
      {
        classNames: 'css-heap-redact',
        id: 5,
        label: 'Volume',
        value: volume,
      },
      {
        id: 6,
        label: 'Transfer Date',
        value: format(parseISO(transferDate), 'MMMM d, yyyy'),
      },
      {
        id: 7,
        label: 'Customer Reference ID',
        value: referenceId,
      },
      {
        id: 8,
        label: 'Inventory Selection',
        value: inventorySelection,
      },
      {
        id: 9,
        label: 'Reconsignment Control',
        value: reconsignmentControl,
      },
    ],
    Origins: [
      {
        id: 1,
        label: 'Origins',
        value: originsToDisplay
          .map(({ originNameLong }) => truncate(originNameLong, LENGTH_LIMIT))
          .join('\n'),
      },
    ],
    // eslint-disable-next-line
    CustomerChain: [
      {
        id: 1,
        label: 'Customer Chain',
        value: chained ? 'Has a Customer Chain' : 'No Customer Chain',
      },
      {
        id: 2,
        label: 'Customer Chain',
        value: chained
          ? chainContactIds.map(({ name }) => truncate(name, LENGTH_LIMIT)).join('\n') || '--'
          : '--',
      },
    ],
  };

  const renderLabel = str => {
    const res = str.replace(/([A-Z])/g, ' $1');
    const final = res.charAt(0).toUpperCase() + res.slice(1);
    return final;
  };

  return (
    <>
      <Box
        onClick={onHeaderClick}
        sx={{
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'space-between',
          minHeight: 48,
          pl: 3.5,
          pr: 3,
          width: '100%',
        }}
      >
        <Typography color={detailsExpanded ? 'textPrimary' : 'primary'} variant="body1">
          {headerTitle}
        </Typography>
        {headerButton && (
          <Button
            sx={{
              minWidth: 'unset',
              p: 0,
            }}
            variant="text"
          >
            {detailsExpanded ? (
              <ExpandMoreRoundedIcon
                position="right"
                sx={{ color: 'secondary.main' }}
                variant="buttonsChild"
              />
            ) : (
              <ExpandLessRoundedIcon
                position="right"
                sx={{ color: 'secondary.main' }}
                variant="buttonsChild"
              />
            )}
          </Button>
        )}
      </Box>
      {Object.keys(summaryFieldsMap).map((section, i) => {
        const fields = summaryFieldsMap[section];
        return (
          <Box
            key={section}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
            }}
          >
            <Box
              sx={{
                alignContent: 'center',
                alignItems: 'center',
                backgroundColor: 'graphic.light',
                display: 'flex',
                justifyContent: 'space-between',
                minHeight: 36,
                pl: 3.5,
                pr: 2,
                width: '100%',
              }}
            >
              <Typography variant="body1">{`${i + 1}. ${renderLabel(section)}`}</Typography>
            </Box>
            {fields.map(({ id, label, value, classNames }) => (
              <Fragment key={id}>
                <Box
                  sx={{
                    alignContent: 'center',
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: 'space-between',
                    minHeight: 48,
                    pl: 3.5,
                    pr: 2,
                    py: 1,
                    width: '100%',
                  }}
                >
                  <Typography variant="body1">{label}</Typography>
                  <Typography
                    className={classNames}
                    sx={{
                      color: 'text.primary',
                      ml: 1,
                      textAlign: 'right',
                      whiteSpace: 'pre-wrap',
                    }}
                    variant="body1"
                  >
                    {value}
                  </Typography>
                </Box>
                <Divider />
              </Fragment>
            ))}
          </Box>
        );
      })}
    </>
  );
};

OrderSummary.propTypes = {
  // data to display in component
  chainContactIds: PropTypes.arrayOf(PropTypes.shape({})),
  createPtoData: PropTypes.shape({
    chained: PropTypes.bool,
    inventorySelection: PropTypes.string,
    originIds: PropTypes.arrayOf(PropTypes.number),
    originatorIdName: PropTypes.string,
    reconsignmentControl: PropTypes.string,
    referenceId: PropTypes.string,
    rvp: PropTypes.number,
    transferDate: PropTypes.string,
    volume: PropTypes.string,
  }).isRequired,
  currentAffiliate: PropTypes.shape({
    customerNameLong: PropTypes.string,
  }),
  currentLocation: PropTypes.shape({
    locationNameLong: PropTypes.string,
  }),
  currentProduct: PropTypes.shape({
    productNameLong: PropTypes.string,
  }),
  // state of the parent slide up drawer, used to determine arrow icon orientation
  detailsExpanded: PropTypes.bool,
  // controls whether to display a button in the header
  headerButton: PropTypes.bool,
  // text to display in header
  headerTitle: PropTypes.string,
  // function called when header is clicked (to toggle expanded/collapsed state of the parent wrapper)
  onHeaderClick: PropTypes.func,
  originatorLocationProductOrigins: PropTypes.arrayOf(PropTypes.shape({})),
  userAffiliates: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

OrderSummary.defaultProps = {
  chainContactIds: [],
  currentAffiliate: {},
  currentLocation: {},
  currentProduct: {},
  detailsExpanded: false,
  headerButton: false,
  headerTitle: '',
  onHeaderClick: null,
  originatorLocationProductOrigins: [],
};
export default OrderSummary;
